import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { PHONE_REGEX } from '../../../../../utils/validations';
import { trimAndCleanSpaces } from '../../../../../utils/trimAndCleanSpaces';
import { updateUserById } from '../../../../../app/store/slices/auth/authSlice';
import { successToastTopRight } from '../../../../../utils/toasts/toasts';

export default function usePersonalInfo() {
    const [avatarPreview, setAvatarPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const user = useSelector((state) => state.auth.user);
    const dispatch = useDispatch();
    const { cities } = useSelector((state) => state.cities);

    const initialValues = {
        name: user?.name || '',
        email: user?.email || '',
        phone: user?.phone || '',
        dob: user?.dob ? dayjs(user.dob, 'YYYY/MM/DD').format('YYYY/MM/DD') : '',
        // hometown: user?.city_id || '',
        hometown: cities.find((city) => city.id === user?.city_id) || null,
        avatar: user?.avatar || null,
        isEditable: false
    };

    const validationSchema = Yup.object({
        name: Yup.string().when('isEditable', {
            is: true,
            then: (schema) => schema.required('Name is required'),
            otherwise: (schema) => schema.notRequired()
        }),
        email: Yup.string().when('isEditable', {
            is: true,
            then: (schema) => schema.email('Invalid email address').required('Email is required'),
            otherwise: (schema) => schema.notRequired()
        }),
        phone: Yup.string().when('isEditable', {
            is: true,
            then: (schema) =>
                schema
                    .matches(PHONE_REGEX, 'Must be a valid 10-digit phone number')
                    .required('Phone number is required'),
            otherwise: (schema) => schema.notRequired()
        }),
        dob: Yup.string().when('isEditable', {
            is: true,
            then: (schema) => schema.required('Date of birth is required'),
            otherwise: (schema) => schema.notRequired()
        }),
        hometown: Yup.mixed().when('isEditable', {
            is: true,
            then: (schema) => schema.required('Hometown is required'),
            otherwise: (schema) => schema.notRequired()
        }),
        avatar: Yup.mixed().notRequired(),
        isEditable: Yup.boolean()
    });

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        validate: (values) => {
            const errors = {};
            if (values.dob) {
                const dob = dayjs(values.dob, 'YYYY/MM/DD');
                if (dayjs().diff(dob, 'year') < 18) {
                    errors.dob = 'Age must be at least 18 years old';
                }
            }
            return errors;
        },
        onSubmit: async (values) => {
            try {
                setLoading(true);

                const formData = new FormData();
                formData.append('name', trimAndCleanSpaces(values.name));
                formData.append('email', trimAndCleanSpaces(values.email).toLowerCase());
                formData.append('phone', values.phone);
                formData.append('dob', dayjs(values.dob, 'YYYY/MM/DD').format('YYYY/MM/DD'));
                formData.append('hometown', values.hometown);

                if (values.avatar instanceof File) {
                    formData.append('avatar', values.avatar);
                }

                formData.append('_method', 'PUT');

                await dispatch(
                    updateUserById({
                        id: user.id,
                        data: formData
                    })
                ).unwrap();

                successToastTopRight('Personal information updated successfully.');

                formik.setFieldValue('isEditable', false);
            } catch (error) {
                // console.error("Error updating personal info:", error);

                formik.setFieldValue('isEditable', true, false);

                if (error?.errors && Array.isArray(error.errors)) {
                    const fieldErrors = {};
                    error.errors.forEach((errObj) => {
                        Object.entries(errObj).forEach(([field, message]) => {
                            fieldErrors[field] = message;
                        });
                    });

                    formik.setErrors(fieldErrors);
                }
            } finally {
                setLoading(false);
            }
        }
    });

    function handleAvatarChange(event) {
        const file = event.target.files[0];
        if (file) {
            formik.setFieldValue('avatar', file);
        } else {
            formik.setFieldValue('avatar', null);
        }
    }

    useEffect(() => {
        if (formik.values.avatar && formik.values.avatar instanceof File) {
            const objectUrl = URL.createObjectURL(formik.values.avatar);
            setAvatarPreview(objectUrl);

            // Cleanup function to revoke object URL
            return () => URL.revokeObjectURL(objectUrl);
        }
    }, [formik.values.avatar]);

    // console.log("Initial values -> ", initialValues)

    return {
        cities,
        formik,
        avatarPreview,
        loading,
        handleAvatarChange
    };
}
